import React from 'react'
import {
	getClassScores,
	type ClassScore,
	type ClientQuestion,
	type StudentAnswer,
} from '@mission.io/question-toolkit'
import type {
	ClassStudentAnalytics,
	AnalyticsQuestionResponse,
} from '@mission.io/mission-toolkit'
import { QuestionGrid } from './QuestionGrid'
import type { QuestionScoreMap } from './types'
import { Header, HeaderCard } from './SharedComponents'

/**
 * QuestionReview - show a grid of questions along with the class score for those questions
 *
 * Any updates made to this component CANNOT include useContext
 * @returns React$Node
 */
export default function QuestionReview({
	analytics,
	questions = [],
}: {
	analytics?: ?ClassStudentAnalytics,
	questions: ?(ClientQuestion[]),
}): React$Node {
	const questionAnswers: { [questionId: string]: StudentAnswer[] } = {}
	const questionScores: QuestionScoreMap = {}

	const studentAnswers: {
		[questionId: string]: { [studentId: string]: AnalyticsQuestionResponse[] },
	} = {}
	Object.keys(analytics?.studentAnalytics || {}).forEach(studentId => {
		analytics?.studentAnalytics?.[studentId]?.questionAnswers?.forEach(
			answer => {
				studentAnswers[answer.questionId] ??= {}
				studentAnswers[answer.questionId][studentId] ??= []
				studentAnswers[answer.questionId][studentId].push(answer)
			}
		)
	})

	Object.keys(studentAnswers).forEach(questionId => {
		let score = 0
		let outOf = 0
		Object.keys(studentAnswers[questionId]).forEach(studentId => {
			studentAnswers[questionId][studentId].sort(
				(a, b) => (a.answerNumber ?? Infinity) - (b.answerNumber ?? Infinity)
			)
			const firstResponse = studentAnswers[questionId][studentId][0]

			if (firstResponse?.score?.score == null) {
				return
			}

			score += firstResponse.score.score
			outOf += firstResponse.score.perfectScore

			questionAnswers[questionId] ??= []
			questionAnswers[questionId].push(firstResponse.answer)
		})

		questionScores[questionId] = {
			correct: score,
			outOf,
		}
	})

	const questionsToShow: Array<ClientQuestion> = (questions || []).filter(
		question => questionScores[question._id]?.outOf
	)

	if (!questionsToShow.length) {
		return null
	}

	const questionClassScores: { [questionId: string]: ClassScore } = {}
	questionsToShow.forEach(question => {
		const questionScore = getClassScores(
			question,
			questionAnswers[question._id]
		)
		if (questionAnswers[question._id] && questionScore) {
			questionClassScores[question._id] = questionScore
		}
	})

	return (
		<div>
			<Header>
				<HeaderCard>
					<h3 className="font-game-headline">Questions</h3>
				</HeaderCard>
			</Header>
			<QuestionGrid
				questions={questionsToShow}
				questionScores={questionScores}
				questionClassScores={questionClassScores}
			/>
		</div>
	)
}
